import React, { useEffect, useRef, useState } from "react";
import { Heart, Save, Plus, Trash2, Pencil, Image as ImageIcon, Calendar, Users, Camera, Video, Ticket, RefreshCw, ExternalLink, Check, X, Flame, ShoppingBag, CreditCard, Headphones, Upload, Music2 } from "lucide-react";
import { api, assetUrl } from "../lib/api";
import { toast } from "sonner";
import { ImageUpload } from "./ImageUpload";
import ArnakStatsPanel from "./ArnakStatsPanel";
import ArnakAdsModerationPanel from "./ArnakAdsModerationPanel";
import AdminAdBannersPanel from "./AdminAdBannersPanel";
import AdminTikTokStudioPanel from "./AdminTikTokStudioPanel";

const inp = "w-full rounded-lg bg-white/[0.04] border border-white/10 px-3 py-2.5 text-sm focus:outline-none focus:border-yellow-400/60";
const lbl = "text-[11px] uppercase tracking-widest text-white/55 font-bold block mb-1";

const SUB_TABS = [
  { key: "stats",    label: "📊 Statistiques", icon: Flame },
  { key: "ads",      label: "🛒 Annonces", icon: ShoppingBag },
  { key: "banners",  label: "📣 Bannières pub", icon: ImageIcon },
  { key: "tiktok",   label: "🎬 Studio TikTok", icon: Flame },
  { key: "settings", label: "Identité", icon: Flame },
  { key: "events",   label: "Concerts", icon: Calendar },
  { key: "tickets",  label: "Réservations", icon: Ticket },
  { key: "news",     label: "Actualités", icon: Pencil },
  { key: "products", label: "Boutique", icon: ShoppingBag },
  { key: "members",  label: "Membres", icon: Users },
  { key: "videos",   label: "Vidéos & Sons", icon: Video },
  { key: "gallery",  label: "Galerie", icon: Camera },
  { key: "partners", label: "Partenaires", icon: ExternalLink },
];

export const AdminArnakPanel = () => {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("stats");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/arnak");
      setData(data);
    } catch (e) { toast.error("Erreur"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading || !data) return <div className="text-white/55 py-8">Chargement…</div>;

  return (
    <div className="space-y-6" data-testid="admin-arnak">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="font-display text-2xl font-black inline-flex items-center gap-2"><Flame size={20} className="text-yellow-400" /> Arnak — Site indépendant</h2>
          <p className="text-xs text-white/55 mt-1">Page publique : <a href="/arnak" target="_blank" rel="noreferrer" className="text-yellow-400 hover:underline">/arnak <ExternalLink size={10} className="inline" /></a></p>
        </div>
        <button onClick={load} className="text-xs text-white/55 hover:text-white inline-flex items-center gap-1"><RefreshCw size={12} /> Rafraîchir</button>
      </div>

      {/* Sub-tabs */}
      <div className="flex items-center gap-1 overflow-x-auto pb-2 border-b border-white/5">
        {SUB_TABS.map((t) => {
          const Icon = t.icon;
          const isActive = tab === t.key;
          const badge = t.key === "tickets" ? data.tickets?.filter((x) => x.status === "pending").length : null;
          return (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`px-4 py-2 text-xs font-bold uppercase tracking-widest inline-flex items-center gap-1.5 whitespace-nowrap border-b-2 transition ${isActive ? "border-yellow-400 text-yellow-400" : "border-transparent text-white/55 hover:text-white"}`}
              data-testid={`arnak-tab-${t.key}`}>
              <Icon size={13} /> {t.label} {badge ? <span className="ml-1 px-1.5 py-0.5 rounded-full bg-yellow-400/25 text-yellow-300 text-[10px]">{badge}</span> : null}
            </button>
          );
        })}
      </div>

      {tab === "stats"    && <ArnakStatsPanel />}
      {tab === "ads"      && <ArnakAdsModerationPanel />}
      {tab === "banners"  && <AdminAdBannersPanel />}
      {tab === "tiktok"   && <AdminTikTokStudioPanel />}
      {tab === "settings" && <SettingsTab data={data} reload={load} />}
      {tab === "events"   && <CrudList items={data.events} reload={load} kind="events" titleLabel="Concerts" cols={EVENT_COLS} />}
      {tab === "tickets"  && <TicketsTab tickets={data.tickets} reload={load} />}
      {tab === "news"     && <CrudList items={data.news || []} reload={load} kind="news" titleLabel="Actualités" cols={NEWS_COLS} />}
      {tab === "products" && <CrudList items={data.products || []} reload={load} kind="products" titleLabel="Boutique — Produits" cols={PRODUCT_COLS} />}
      {tab === "members"  && <CrudList items={data.members} reload={load} kind="members" titleLabel="Membres" cols={MEMBER_COLS} />}
      {tab === "videos"   && <CrudList items={data.videos} reload={load} kind="videos" titleLabel="Vidéos" cols={VIDEO_COLS} />}
      {tab === "gallery"  && <GalleryManager items={data.gallery} reload={load} />}
      {tab === "partners" && <CrudList items={data.partners || []} reload={load} kind="partners" titleLabel="Partenaires / Sponsors" cols={PARTNER_COLS} />}
    </div>
  );
};

// ─── SETTINGS TAB ───
const SettingsTab = ({ data, reload }) => {
  const [form, setForm] = useState(data.settings);
  const [saving, setSaving] = useState(false);
  useEffect(() => setForm(data.settings), [data.settings]);

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/admin/arnak/settings", form);
      toast.success("Réglages enregistrés ✨"); reload();
    } catch { toast.error("Erreur"); }
    finally { setSaving(false); }
  };
  const f = (k) => ({ value: form[k] || "", onChange: (e) => setForm((s) => ({ ...s, [k]: e.target.value })) });

  return (
    <section className="space-y-5">
      <div className="grid md:grid-cols-2 gap-4">
        <div><label className={lbl}>Nom du groupe</label><input {...f("name")} className={inp} data-testid="arnak-set-name" /></div>
        <div><label className={lbl}>Slogan / Tagline</label><input {...f("tagline")} className={inp} data-testid="arnak-set-tagline" /></div>
        <div><label className={lbl}>Genre musical</label><input {...f("genre")} className={inp} data-testid="arnak-set-genre" /></div>
        <div><label className={lbl}>Année de formation</label><input {...f("founded")} className={inp} placeholder="ex: 2015" /></div>
        <div><label className={lbl}>Ville</label><input {...f("city")} className={inp} /></div>
        <div><label className={lbl}>Pays</label><input {...f("country")} className={inp} placeholder="Madagascar" /></div>
        <div className="md:col-span-2"><label className={lbl}>Bio (3-5 phrases)</label><textarea {...f("bio")} rows={4} className={inp} data-testid="arnak-set-bio" /></div>
        <ImageUpload value={form.logo_url} onChange={(url) => setForm((s) => ({ ...s, logo_url: url }))} kind="arnak" aspect="square" label="Logo du groupe" hint="PNG transparent recommandé · max 5 Mo" testId="arnak-logo-upload" />
        <ImageUpload value={form.cover_url} onChange={(url) => setForm((s) => ({ ...s, cover_url: url }))} kind="arnak" aspect="wide" label="Photo de couverture (hero)" hint="Image large pour le bandeau d'accueil" testId="arnak-cover-upload" />
      </div>

      <fieldset className="border border-white/10 rounded-xl p-4">
        <legend className="text-[10px] uppercase tracking-widest text-yellow-400 font-bold px-2">Réseaux sociaux</legend>
        <div className="grid md:grid-cols-2 gap-3 mt-2">
          <div><label className={lbl}>Facebook</label><input {...f("fb_url")} className={inp} placeholder="https://facebook.com/arnak110" /></div>
          <div><label className={lbl}>Instagram</label><input {...f("ig_url")} className={inp} /></div>
          <div><label className={lbl}>TikTok</label><input {...f("tt_url")} className={inp} /></div>
          <div><label className={lbl}>YouTube</label><input {...f("yt_url")} className={inp} /></div>
        </div>
        <div className="mt-3 pt-3 border-t border-white/5">
          <label className={lbl}>📘 Page Facebook (intégration sur le site)</label>
          <input
            {...f("facebook_page_url")}
            className={inp}
            placeholder="https://www.facebook.com/arnak110"
            data-testid="arnak-fb-page-url"
          />
          <p className="text-[10px] text-white/45 mt-1">
            Cette URL active le widget Facebook officiel sur la page d'accueil : flux des dernières publications + bouton "J'aime" intégré. Laisser vide pour désactiver.
          </p>
        </div>
        <div className="mt-3 pt-3 border-t border-white/5">
          <label className={lbl}>👥 Nombre d'abonnés Facebook (affiché dans le menu)</label>
          <input
            type="number"
            min="0"
            {...f("facebook_followers_count")}
            className={inp}
            placeholder="ex: 12500"
            data-testid="arnak-fb-followers-count"
          />
          <p className="text-[10px] text-white/45 mt-1">
            Entrez le nombre exact (ex: <code className="text-yellow-300">12500</code> → s'affichera <code className="text-yellow-300">12,5K abonnés</code>). À mettre à jour manuellement depuis la page Facebook ARNAK. Laisser vide pour masquer.
          </p>
        </div>
      </fieldset>

      <fieldset className="border border-white/10 rounded-xl p-4">
        <legend className="text-[10px] uppercase tracking-widest text-yellow-400 font-bold px-2">Booking / Management</legend>
        <div className="grid md:grid-cols-3 gap-3 mt-2">
          <div><label className={lbl}>Email booking</label><input type="email" {...f("booking_email")} className={inp} placeholder="booking@…" /></div>
          <div><label className={lbl}>Téléphone</label><input {...f("booking_phone")} className={inp} placeholder="+261…" /></div>
          <div><label className={lbl}>WhatsApp</label><input {...f("whatsapp")} className={inp} placeholder="+261…" /></div>
        </div>
      </fieldset>

      <fieldset className="border border-white/10 rounded-xl p-4">
        <legend className="text-[10px] uppercase tracking-widest text-yellow-400 font-bold px-2 inline-flex items-center gap-1.5"><CreditCard size={11} /> Paiement Stripe</legend>
        <div className="grid gap-3 mt-2">
          <div>
            <label className={lbl}>Lien de paiement Stripe (Payment Link)</label>
            <input
              type="url"
              {...f("stripe_payment_url")}
              className={inp}
              placeholder="https://buy.stripe.com/…"
              data-testid="arnak-stripe-url"
            />
            <p className="text-[10px] text-white/45 mt-1">
              Va sur <a href="https://dashboard.stripe.com/payment-links" target="_blank" rel="noreferrer" className="text-yellow-400 hover:underline">dashboard.stripe.com → Payment Links</a>, crée un lien, et colle l'URL ici. Un bouton "Payer avec Stripe" sera ajouté aux concerts.
            </p>
          </div>
        </div>
      </fieldset>

      <fieldset className="border border-white/10 rounded-xl p-4">
        <legend className="text-[10px] uppercase tracking-widest text-yellow-400 font-bold px-2 inline-flex items-center gap-1.5"><ShoppingBag size={11} /> Boutique</legend>
        <div className="grid gap-4 mt-2">
          <div>
            <label className={lbl}>Statut de la boutique</label>
            <select {...f("shop_status")} className={inp} data-testid="arnak-shop-status">
              <option value="soon">Bientôt disponible (badge affiché, pas d'achat)</option>
              <option value="live">Boutique ouverte (achats possibles)</option>
              <option value="hidden">Masquée (section boutique invisible)</option>
            </select>
            <p className="text-[10px] text-white/45 mt-1">Configure les produits dans l'onglet "Boutique" du menu.</p>
          </div>
          <ImageUpload
            value={form.shop_placeholder_image}
            onChange={(url) => setForm((s) => ({ ...s, shop_placeholder_image: url }))}
            kind="arnak"
            aspect="wide"
            label="Image provisoire (affichée tant qu'il n'y a pas de produit)"
            hint="Ex : visuel teaser, mockup T-shirt, photo studio… · max 5 Mo"
            testId="arnak-shop-placeholder-upload"
          />
        </div>
      </fieldset>

      <fieldset className="border border-white/10 rounded-xl p-4">
        <legend className="text-[10px] uppercase tracking-widest text-yellow-400 font-bold px-2 inline-flex items-center gap-1.5"><ExternalLink size={11} /> Partage sur réseaux sociaux (SEO)</legend>
        <div className="grid gap-3 mt-2">
          <p className="text-[10px] text-white/55">Ce qui s'affichera quand quelqu'un partage <code className="text-yellow-300">arnakclan.com</code> sur Facebook, Messenger, WhatsApp, Twitter, etc.</p>
          <div>
            <label className={lbl}>Titre de partage</label>
            <input
              type="text"
              {...f("share_title")}
              className={inp}
              placeholder="ex: ARNAK — Rap Gasy & Hip-Hop"
              maxLength={120}
              data-testid="arnak-share-title"
            />
          </div>
          <div>
            <label className={lbl}>Description de partage</label>
            <textarea
              {...f("share_description")}
              className={inp}
              rows={3}
              placeholder="Texte affiché sous le titre dans l'aperçu du lien (idéal 150-200 caractères)"
              maxLength={300}
              data-testid="arnak-share-description"
            />
          </div>
          <ImageUpload
            value={form.share_image}
            onChange={(url) => setForm((s) => ({ ...s, share_image: url }))}
            kind="arnak"
            aspect="wide"
            label="Image de partage (recommandé 1200×630)"
            hint="Affichée dans l'aperçu du lien. Idéal : photo du groupe ou affiche de concert."
            testId="arnak-share-image-upload"
          />
        </div>
      </fieldset>

      <fieldset className="border border-yellow-400/20 rounded-xl p-4 bg-yellow-400/[0.02]">
        <legend className="text-[10px] uppercase tracking-widest text-yellow-400 font-bold px-2 inline-flex items-center gap-1.5">
          💬 Message privé de bienvenue (DM auto)
        </legend>
        <p className="text-[10px] text-white/55 mt-2 mb-4">
          Envoie automatiquement un message privé à chaque nouvel inscrit, depuis ton compte ARNAK Manager. Variables disponibles : <code className="text-yellow-300">{`{prenom}`}</code>, <code className="text-yellow-300">{`{nom}`}</code>.
        </p>
        <div className="grid gap-3">
          <div>
            <label className={lbl}>Activer le DM auto de bienvenue ?</label>
            <select
              value={form.arnak_welcome_dm_enabled || "0"}
              onChange={(e) => setForm((s) => ({ ...s, arnak_welcome_dm_enabled: e.target.value }))}
              className={inp}
              data-testid="arnak-welcome-dm-enabled"
            >
              <option value="0">Désactivé (aucun DM auto)</option>
              <option value="1">Activé (DM auto à chaque nouvelle inscription)</option>
            </select>
          </div>
          <div>
            <label className={lbl}>Texte du message</label>
            <textarea
              {...f("arnak_welcome_dm_text")}
              rows={6}
              className={inp}
              placeholder="Salut {prenom} ! Bienvenue dans la famille ARNAK 🔥"
              data-testid="arnak-welcome-dm-text"
            />
            <p className="text-[10px] text-white/45 mt-1">
              Astuce : sois chaleureux et personnel. Un membre qui reçoit un DM dès son inscription se sent accueilli et reste plus longtemps.
            </p>
          </div>
        </div>
      </fieldset>

      <fieldset className="border border-yellow-400/20 rounded-xl p-4 bg-yellow-400/[0.02]">
        <legend className="text-[10px] uppercase tracking-widest text-yellow-400 font-bold px-2 inline-flex items-center gap-1.5">
          🎙️ RADO Production — Studio partenaire
        </legend>
        <p className="text-[10px] text-white/55 mt-2 mb-4">
          Page dédiée accessible via <code className="text-yellow-300">/rado-production</code> et liée depuis le menu Arnak + section Partenaires + footer. Le flux Facebook du studio est affiché en live.
        </p>
        <div className="grid gap-3">
          <div>
            <label className={lbl}>Section visible sur le site ?</label>
            <select
              value={form.rado_active || "1"}
              onChange={(e) => setForm((s) => ({ ...s, rado_active: e.target.value }))}
              className={inp}
              data-testid="rado-active"
            >
              <option value="1">Visible (menu + page + section Partenaires)</option>
              <option value="0">Masqué (page accessible mais aucun lien)</option>
            </select>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className={lbl}>Nom du studio</label>
              <input {...f("rado_name")} className={inp} placeholder="RADO Production" data-testid="rado-name" />
            </div>
            <div>
              <label className={lbl}>Slogan / Tagline</label>
              <input {...f("rado_tagline")} className={inp} placeholder="Studio de production · Toamasina" data-testid="rado-tagline" />
            </div>
            <div className="md:col-span-2">
              <label className={lbl}>Bio courte (2-4 phrases)</label>
              <textarea {...f("rado_bio")} rows={3} className={inp} placeholder="Studio d'enregistrement…" data-testid="rado-bio" />
            </div>
            <div className="md:col-span-2">
              <label className={lbl}>Ville / Pays</label>
              <input {...f("rado_city")} className={inp} placeholder="Toamasina, Madagascar" />
            </div>
          </div>
          <ImageUpload
            value={form.rado_logo_url}
            onChange={(url) => setForm((s) => ({ ...s, rado_logo_url: url }))}
            kind="arnak"
            aspect="square"
            label="Logo du studio"
            hint="PNG transparent recommandé · max 5 Mo"
            testId="rado-logo-upload"
          />
          <ImageUpload
            value={form.rado_cover_url}
            onChange={(url) => setForm((s) => ({ ...s, rado_cover_url: url }))}
            kind="arnak"
            aspect="wide"
            label="Photo de couverture (hero)"
            hint="Image large pour le bandeau d'accueil"
            testId="rado-cover-upload"
          />
          <div>
            <label className={lbl}>📘 URL Page Facebook officielle</label>
            <input
              type="url"
              {...f("rado_fb_url")}
              className={inp}
              placeholder="https://www.facebook.com/Radostudiopro"
              data-testid="rado-fb-url"
            />
            <p className="text-[10px] text-white/45 mt-1">
              C'est l'URL utilisée pour le flux d'actualités intégré et le bouton "Suivre".
            </p>
          </div>
          <div>
            <label className={lbl}>👥 Nombre d'abonnés Facebook (affiché dans la nav RADO)</label>
            <input
              type="number"
              min="0"
              {...f("rado_facebook_followers_count")}
              className={inp}
              placeholder="ex: 5400"
              data-testid="rado-fb-followers"
            />
            <p className="text-[10px] text-white/45 mt-1">
              Mise à jour manuelle. Laisser vide pour masquer le compteur.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-3">
            <div>
              <label className={lbl}>Email contact</label>
              <input type="email" {...f("rado_email")} className={inp} placeholder="contact@…" />
            </div>
            <div>
              <label className={lbl}>Téléphone</label>
              <input {...f("rado_phone")} className={inp} placeholder="+261…" />
            </div>
            <div>
              <label className={lbl}>WhatsApp</label>
              <input {...f("rado_whatsapp")} className={inp} placeholder="+261…" />
            </div>
          </div>
        </div>
      </fieldset>

      <button onClick={save} disabled={saving} className="bg-yellow-400 hover:bg-yellow-300 text-black font-black uppercase tracking-widest text-xs px-6 py-3 inline-flex items-center gap-2 disabled:opacity-50" data-testid="arnak-set-save">
        {saving ? "Enregistrement…" : <><Save size={13} /> Enregistrer</>}
      </button>
    </section>
  );
};

// ─── GALLERY MANAGER — multi-upload + albums (rubriques) ───
const GalleryManager = ({ items, reload }) => {
  const [filterAlbum, setFilterAlbum] = useState("__all");
  const [uploadAlbum, setUploadAlbum] = useState("");
  const [newAlbum, setNewAlbum] = useState("");
  const [showNewAlbum, setShowNewAlbum] = useState(false);
  const [editing, setEditing] = useState(null);
  const fileInputRef = React.useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });

  // Liste unique d'albums existants
  const albums = React.useMemo(() => {
    const set = new Set();
    items.forEach((p) => { if (p.album) set.add(p.album); });
    return Array.from(set).sort((a, b) => a.localeCompare(b, "fr"));
  }, [items]);

  // Photos filtrées
  const filtered = React.useMemo(() => {
    if (filterAlbum === "__all") return items;
    if (filterAlbum === "__none") return items.filter((p) => !p.album);
    return items.filter((p) => p.album === filterAlbum);
  }, [items, filterAlbum]);

  // Grouper pour affichage
  const grouped = React.useMemo(() => {
    const map = {};
    filtered.forEach((p) => {
      const k = p.album || "__sansrubrique";
      if (!map[k]) map[k] = [];
      map[k].push(p);
    });
    return map;
  }, [filtered]);

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    const targetAlbum = uploadAlbum.trim();
    setUploading(true);
    setProgress({ done: 0, total: files.length });
    const uploadedUrls = [];
    for (const file of files) {
      if (!file.type.startsWith("image/")) continue;
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`"${file.name}" trop lourde (max 5 Mo)`);
        setProgress((p) => ({ ...p, done: p.done + 1 }));
        continue;
      }
      try {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("kind", "arnak");
        const { data } = await api.post("/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
        if (data?.url) uploadedUrls.push(data.url);
      } catch (e) {
        toast.error(`Échec : ${file.name}`);
      }
      setProgress((p) => ({ ...p, done: p.done + 1 }));
    }
    if (uploadedUrls.length > 0) {
      try {
        const r = await api.post("/admin/arnak/gallery/bulk", { album: targetAlbum || null, urls: uploadedUrls });
        toast.success(`${r.data.count || uploadedUrls.length} photo${uploadedUrls.length > 1 ? "s" : ""} ajoutée${uploadedUrls.length > 1 ? "s" : ""}`);
        reload();
      } catch (e) {
        toast.error(e?.response?.data?.error || "Erreur d'enregistrement");
      }
    }
    setUploading(false);
    setProgress({ done: 0, total: 0 });
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const onDelete = async (photoId) => {
    if (!window.confirm("Supprimer cette photo ?")) return;
    try {
      await api.post("/admin/arnak/gallery", { _action: "delete", photo_id: photoId });
      toast.success("Supprimée");
      reload();
    } catch { toast.error("Erreur"); }
  };

  return (
    <section className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-bold text-lg">Galerie ({items.length})</h3>
          <p className="text-xs text-white/55 mt-0.5">{albums.length} rubrique{albums.length > 1 ? "s" : ""}</p>
        </div>
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="bg-yellow-400 hover:bg-yellow-300 text-black font-black uppercase tracking-widest text-xs px-4 py-2 inline-flex items-center gap-1.5 disabled:opacity-50"
          data-testid="gallery-bulk-upload-btn"
        >
          <Plus size={12} /> {uploading ? `Envoi ${progress.done}/${progress.total}…` : "Ajouter photos"}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => handleFiles(Array.from(e.target.files || []))}
        />
      </div>

      {/* Sélecteur rubrique de destination + création de rubrique */}
      <div className="border border-white/10 rounded-xl p-4 space-y-3 bg-white/[0.02]">
        <p className="text-[10px] uppercase tracking-widest text-yellow-400 font-bold">Rubrique de destination (pour les prochains uploads)</p>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setUploadAlbum("")}
            className={`px-3 py-1.5 text-xs font-bold uppercase tracking-widest transition ${uploadAlbum === "" ? "bg-yellow-400 text-black" : "bg-white/5 text-white/70 hover:bg-white/10"}`}
          >
            Sans rubrique
          </button>
          {albums.map((a) => (
            <button
              key={a}
              type="button"
              onClick={() => setUploadAlbum(a)}
              className={`px-3 py-1.5 text-xs font-bold uppercase tracking-widest transition ${uploadAlbum === a ? "bg-yellow-400 text-black" : "bg-white/5 text-white/70 hover:bg-white/10"}`}
            >
              {a}
            </button>
          ))}
          {showNewAlbum ? (
            <div className="flex gap-1">
              <input
                value={newAlbum}
                onChange={(e) => setNewAlbum(e.target.value)}
                placeholder="Nom rubrique"
                className="px-3 py-1.5 text-xs font-bold bg-zinc-900 border border-yellow-400/60 text-white focus:outline-none"
                autoFocus
                onKeyDown={(e) => { if (e.key === "Enter" && newAlbum.trim()) { setUploadAlbum(newAlbum.trim()); setShowNewAlbum(false); setNewAlbum(""); } }}
              />
              <button onClick={() => { if (newAlbum.trim()) { setUploadAlbum(newAlbum.trim()); setShowNewAlbum(false); setNewAlbum(""); } }} className="px-3 py-1.5 bg-yellow-400 text-black text-xs font-bold"><Check size={12} /></button>
              <button onClick={() => { setShowNewAlbum(false); setNewAlbum(""); }} className="px-3 py-1.5 bg-white/10 text-white text-xs"><X size={12} /></button>
            </div>
          ) : (
            <button onClick={() => setShowNewAlbum(true)} className="px-3 py-1.5 text-xs font-bold uppercase tracking-widest bg-yellow-400/15 text-yellow-400 hover:bg-yellow-400/25 border border-dashed border-yellow-400/40 inline-flex items-center gap-1" data-testid="gallery-new-album-btn">
              <Plus size={11} /> Nouvelle rubrique
            </button>
          )}
        </div>
        <p className="text-[10px] text-white/45">Rubrique sélectionnée : <span className="text-yellow-400 font-bold">{uploadAlbum || "Sans rubrique"}</span> — toutes les photos uploadées seront classées ici. Tu pourras les déplacer ensuite.</p>
      </div>

      {/* Filtre d'affichage */}
      {(albums.length > 0 || items.some((p) => !p.album)) && (
        <div className="flex flex-wrap items-center gap-2 border-b border-white/5 pb-3">
          <span className="text-[10px] uppercase tracking-widest text-white/45 font-bold mr-2">Afficher :</span>
          <button onClick={() => setFilterAlbum("__all")} className={`px-2.5 py-1 text-[10px] uppercase tracking-widest font-bold transition ${filterAlbum === "__all" ? "bg-white text-black" : "bg-white/5 text-white/70 hover:bg-white/10"}`}>
            Toutes ({items.length})
          </button>
          {albums.map((a) => {
            const c = items.filter((p) => p.album === a).length;
            return (
              <button key={a} onClick={() => setFilterAlbum(a)} className={`px-2.5 py-1 text-[10px] uppercase tracking-widest font-bold transition ${filterAlbum === a ? "bg-white text-black" : "bg-white/5 text-white/70 hover:bg-white/10"}`}>
                {a} ({c})
              </button>
            );
          })}
          {items.some((p) => !p.album) && (
            <button onClick={() => setFilterAlbum("__none")} className={`px-2.5 py-1 text-[10px] uppercase tracking-widest font-bold transition ${filterAlbum === "__none" ? "bg-white text-black" : "bg-white/5 text-white/70 hover:bg-white/10"}`}>
              Sans rubrique ({items.filter((p) => !p.album).length})
            </button>
          )}
        </div>
      )}

      {/* Affichage groupé par rubrique */}
      {items.length === 0 ? (
        <div className="text-center py-12 text-white/40 text-sm border border-dashed border-white/10 rounded-xl">
          Aucune photo — clique sur "Ajouter photos" pour téléverser. Tu peux sélectionner plusieurs photos d'un coup.
        </div>
      ) : (
        Object.keys(grouped).sort().map((alb) => (
          <div key={alb} className="space-y-3">
            <div className="flex items-center justify-between border-b border-yellow-400/20 pb-2">
              <h4 className="font-black text-sm uppercase tracking-widest text-yellow-400">
                {alb === "__sansrubrique" ? "Sans rubrique" : alb}
              </h4>
              <span className="text-[10px] text-white/45 font-bold">{grouped[alb].length} photo{grouped[alb].length > 1 ? "s" : ""}</span>
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2">
              {grouped[alb].map((p) => (
                <div key={p.photo_id} className="relative group aspect-square overflow-hidden bg-white/5 border border-white/10 hover:border-yellow-400/60 transition" data-testid={`gallery-item-${p.photo_id}`}>
                  <img src={p.image_url} alt={p.caption || ""} loading="lazy" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/70 transition flex flex-col items-center justify-center gap-1.5 opacity-0 group-hover:opacity-100">
                    <button onClick={() => setEditing(p)} className="px-3 py-1.5 rounded bg-yellow-400 text-black text-[10px] uppercase tracking-widest font-bold inline-flex items-center gap-1"><Pencil size={11} /> Modifier</button>
                    <button onClick={() => onDelete(p.photo_id)} className="px-3 py-1.5 rounded bg-red-500/90 text-white text-[10px] uppercase tracking-widest font-bold inline-flex items-center gap-1"><Trash2 size={11} /> Suppr.</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))
      )}

      {editing && <CrudForm kind="gallery" cols={PHOTO_COLS} initial={editing} onClose={() => setEditing(null)} reload={reload} />}
    </section>
  );
};

// ─── COL DEFS ───
const MEMBER_COLS = [
  { key: "name", label: "Nom", required: true, type: "text" },
  { key: "role", label: "Rôle / Pseudo", type: "text", placeholder: "MC, Beatmaker…" },
  { key: "photo_url", label: "Photo URL", type: "url" },
  { key: "bio", label: "Bio", type: "textarea" },
  { key: "sort_order", label: "Ordre", type: "number", default: 0 },
];
const EVENT_COLS = [
  { key: "title", label: "Titre", required: true, type: "text" },
  { key: "tour_name", label: "Nom de la tournée", type: "text", placeholder: "ex: Ala Satroka Tour" },
  { key: "event_date", label: "Date & heure", required: true, type: "datetime-local" },
  { key: "venue", label: "Lieu (salle)", type: "text" },
  { key: "venue_logo_url", label: "Logo de la salle", type: "url" },
  { key: "venue_lat", label: "Latitude GPS (ex: -18.879)", type: "number", placeholder: "-18.879" },
  { key: "venue_lng", label: "Longitude GPS (ex: 47.508)", type: "number", placeholder: "47.508" },
  { key: "city", label: "Ville", type: "text" },
  { key: "country", label: "Pays", type: "text", default: "MG" },
  { key: "price_ar", label: "Prix (Ariary)", type: "number" },
  { key: "capacity", label: "Capacité", type: "number" },
  { key: "image_url", label: "Affiche URL", type: "url" },
  { key: "ticket_url", label: "Lien billetterie externe", type: "url" },
  { key: "description", label: "Description", type: "textarea" },
  { key: "sold_out", label: "Complet ?", type: "checkbox" },
];
const PHOTO_COLS = [
  { key: "image_url", label: "URL de l'image", required: true, type: "url" },
  { key: "album", label: "Rubrique (album)", type: "text", placeholder: "ex: Concert Ala Satroka" },
  { key: "caption", label: "Légende", type: "text" },
  { key: "sort_order", label: "Ordre", type: "number", default: 0 },
];
const VIDEO_COLS = [
  { key: "title", label: "Titre", required: true, type: "text" },
  // Champs cachés derrière le sélecteur de type (gérés dans CrudForm pour les vidéos)
  { key: "embed_url", label: "URL embed (YouTube /embed/ ou TikTok)", type: "url", placeholder: "https://www.youtube.com/embed/…", videoOnly: true },
  { key: "thumbnail_url", label: "Miniature URL", type: "url" },
  { key: "sort_order", label: "Ordre", type: "number", default: 0 },
];
const NEWS_COLS = [
  { key: "title", label: "Titre", required: true, type: "text" },
  { key: "excerpt", label: "Accroche (résumé)", type: "textarea", placeholder: "1-2 phrases qui résument l'actu" },
  { key: "body", label: "Contenu complet", type: "textarea" },
  { key: "image_url", label: "Image", type: "url" },
  { key: "link_url", label: "Lien externe (optionnel)", type: "url", placeholder: "https://…" },
  { key: "published_at", label: "Date de publication", type: "datetime-local" },
  { key: "sort_order", label: "Ordre", type: "number", default: 0 },
];
const PARTNER_COLS = [
  { key: "name", label: "Nom du partenaire", required: true, type: "text" },
  { key: "logo_url", label: "Logo", type: "url" },
  { key: "link_url", label: "Lien (site web)", type: "url" },
  { key: "sort_order", label: "Ordre", type: "number", default: 0 },
];
const PRODUCT_COLS = [
  { key: "name", label: "Nom du produit", required: true, type: "text", placeholder: "ex: T-shirt ARNAK noir" },
  { key: "category", label: "Catégorie", type: "text", placeholder: "T-shirt / Casquette / Sweat / Accessoire…" },
  { key: "description", label: "Description", type: "textarea", placeholder: "Matière, tailles disponibles, infos produit…" },
  { key: "price_ar", label: "Prix (Ariary)", type: "number", placeholder: "ex: 35000" },
  { key: "price_eur", label: "Prix (Euro)", type: "number", placeholder: "ex: 12.50" },
  { key: "image_url", label: "Photo du produit", type: "url" },
  { key: "available", label: "Disponible à la vente (sinon affiché en \"Bientôt\")", type: "checkbox" },
  { key: "sort_order", label: "Ordre d'affichage", type: "number", default: 0 },
];

// ─── AUDIO UPLOAD COMPONENT ───
const AudioUpload = ({ value, onChange, hint, testId }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) { toast.error("Fichier trop lourd (max 25 Mo)"); return; }
    setUploading(true); setProgress(0);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload/audio", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (p) => {
          if (p.total) setProgress(Math.round((p.loaded / p.total) * 100));
        },
      });
      onChange(data.url);
      toast.success("Audio uploadé");
    } catch (err) {
      toast.error(err?.response?.data?.error || "Erreur upload");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div data-testid={testId}>
      {value ? (
        <div className="bg-yellow-400/[0.04] border border-yellow-400/20 rounded-lg p-3 flex items-center gap-3">
          <div className="w-12 h-12 rounded bg-yellow-400/15 flex items-center justify-center shrink-0">
            <Music2 size={20} className="text-yellow-300" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-bold truncate">{value.split("/").pop()}</p>
            <audio src={assetUrl(value)} controls className="w-full mt-1 h-8" />
          </div>
          <button type="button" onClick={() => onChange("")} className="p-2 hover:bg-red-500/15 rounded text-white/55 hover:text-red-400">
            <Trash2 size={14} />
          </button>
        </div>
      ) : (
        <label className="block cursor-pointer">
          <div className="border-2 border-dashed border-yellow-400/30 hover:border-yellow-400/60 rounded-lg p-5 text-center bg-yellow-400/[0.02] hover:bg-yellow-400/[0.05] transition">
            {uploading ? (
              <>
                <p className="text-sm font-bold text-yellow-300">Upload en cours… {progress}%</p>
                <div className="mt-2 w-full bg-white/10 h-1 rounded overflow-hidden">
                  <div className="bg-yellow-400 h-full transition-all" style={{ width: `${progress}%` }} />
                </div>
              </>
            ) : (
              <>
                <Upload size={22} className="mx-auto mb-2 text-yellow-300" />
                <p className="text-sm font-bold">Choisir un fichier audio</p>
                <p className="text-[11px] text-white/45 mt-1">{hint || "MP3 / WAV / OGG / M4A · max 25 Mo"}</p>
              </>
            )}
          </div>
          <input ref={inputRef} type="file" accept="audio/*,.mp3,.wav,.ogg,.m4a,.aac" onChange={handle} className="hidden" disabled={uploading} />
        </label>
      )}
    </div>
  );
};

// ─── CRUD LIST ───
const CrudList = ({ items, reload, kind, titleLabel, cols }) => {
  const [editing, setEditing] = useState(null); // null = none, {} = new, {...} = edit
  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-bold text-lg">{titleLabel} ({items.length})</h3>
        <button onClick={() => setEditing({})} className="bg-yellow-400 hover:bg-yellow-300 text-black font-black uppercase tracking-widest text-xs px-4 py-2 inline-flex items-center gap-1.5" data-testid={`arnak-${kind}-add`}>
          <Plus size={12} /> Ajouter
        </button>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-12 text-white/40 text-sm border border-dashed border-white/10 rounded-xl">Vide — clique "Ajouter" pour commencer</div>
      ) : (
        <div className="space-y-2">
          {items.map((it) => <ItemRow key={it.member_id || it.event_id || it.photo_id || it.video_id || it.product_id || it.news_id || it.partner_id} item={it} cols={cols} kind={kind} onEdit={() => setEditing(it)} reload={reload} />)}
        </div>
      )}

      {editing && <CrudForm kind={kind} cols={cols} initial={editing} onClose={() => setEditing(null)} reload={reload} />}
    </section>
  );
};

const ItemRow = ({ item, cols, kind, onEdit, reload }) => {
  const [showMedia, setShowMedia] = useState(false);
  const idKey = { members: "member_id", events: "event_id", gallery: "photo_id", videos: "video_id", news: "news_id", partners: "partner_id", products: "product_id" }[kind];
  const onDelete = async () => {
    if (!window.confirm("Supprimer cet élément ?")) return;
    try {
      await api.post(`/admin/arnak/${kind}`, { _action: "delete", [idKey]: item[idKey] });
      toast.success("Supprimé"); reload();
    } catch { toast.error("Erreur"); }
  };
  const thumb = item.photo_url || item.image_url || item.thumbnail_url;
  const title = item.name || item.title || item.caption || "(sans titre)";
  const sub = item.role || (item.event_date ? new Date(item.event_date.replace(" ", "T")).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }) : item.embed_url);
  const mediaCount = (item.media || []).length;
  return (
    <>
      <div className="flex items-center gap-3 p-3 rounded-lg bg-white/[0.02] border border-white/10 hover:border-white/20 transition">
        {thumb && <img src={thumb} alt="" className="w-14 h-14 object-cover rounded-lg shrink-0" />}
        <div className="flex-1 min-w-0">
          <p className="font-bold truncate">{title}</p>
          {sub && <p className="text-xs text-white/55 truncate">{sub}</p>}
        </div>
        {(kind === "events" || kind === "news") && (
          <button onClick={() => setShowMedia(true)} className="px-2.5 py-1.5 rounded text-[10px] uppercase tracking-widest font-bold bg-orange-500/20 hover:bg-orange-500/30 text-orange-300 inline-flex items-center gap-1.5 transition" title="Photos & vidéos" data-testid={`arnak-${kind}-media-${item[idKey]}`}>
            <Camera size={12} /> Médias {mediaCount > 0 && <span className="ml-0.5 px-1.5 py-0.5 rounded-full bg-orange-500 text-white text-[9px]">{mediaCount}</span>}
          </button>
        )}
        <button onClick={onEdit} className="p-2 rounded hover:bg-white/5 text-white/55 hover:text-yellow-400" title="Modifier" data-testid={`arnak-${kind}-edit`}><Pencil size={14} /></button>
        <button onClick={onDelete} className="p-2 rounded hover:bg-red-500/10 text-white/55 hover:text-red-400" title="Supprimer" data-testid={`arnak-${kind}-del`}><Trash2 size={14} /></button>
      </div>
      {showMedia && <MediaManager parentKind={kind} parent={item} onClose={() => setShowMedia(false)} reload={reload} />}
    </>
  );
};

// ─── MEDIA MANAGER — gère photos/vidéos d'un concert OU d'une actualité ───
// parentKind: "events" | "news"
const MediaManager = ({ parentKind, parent, onClose, reload }) => {
  const isEvent = parentKind === "events";
  const parentIdKey = isEvent ? "event_id" : "news_id";
  const apiSub = isEvent ? "event_media" : "news_media";
  const parentId = parent[parentIdKey];
  const parentTitle = parent.title || "(sans titre)";

  const [items, setItems] = useState(parent.media || []);
  const [type, setType] = useState("photo");
  const [url, setUrl] = useState("");
  const [caption, setCaption] = useState("");
  const [saving, setSaving] = useState(false);

  const refresh = async () => {
    try {
      const { data } = await api.get("/admin/arnak");
      const list = isEvent ? data.events : data.news;
      const found = (list || []).find((x) => x[parentIdKey] === parentId);
      setItems(found?.media || []);
    } catch { /* ignore */ }
  };

  const add = async (e) => {
    e.preventDefault();
    if (!url.trim()) return;
    setSaving(true);
    try {
      await api.post(`/admin/arnak/${apiSub}`, {
        [parentIdKey]: parentId, type, url: url.trim(), caption: caption.trim() || null, sort_order: items.length,
      });
      setUrl(""); setCaption("");
      toast.success("Ajouté");
      await refresh();
      reload();
    } catch (e) { toast.error(e?.response?.data?.error || "Erreur"); }
    finally { setSaving(false); }
  };

  const remove = async (mediaId) => {
    if (!window.confirm("Supprimer ce média ?")) return;
    try {
      await api.post(`/admin/arnak/${apiSub}`, { _action: "delete", media_id: mediaId });
      toast.success("Supprimé");
      await refresh();
      reload();
    } catch { toast.error("Erreur"); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-zinc-950 border border-white/15 rounded-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto space-y-5" data-testid={`arnak-${apiSub}-modal`}>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-orange-400 font-bold">{isEvent ? "Médias du concert" : "Médias de l'actualité"}</p>
            <h3 className="font-bold text-lg">{parentTitle}</h3>
          </div>
          <button onClick={onClose} className="text-white/50 hover:text-white"><X size={18} /></button>
        </div>

        <div className="space-y-2">
          {items.length === 0 ? (
            <p className="text-center py-8 text-white/40 text-sm border border-dashed border-white/10 rounded-xl">Aucun média — ajoute photos et vidéos ci-dessous</p>
          ) : items.map((m) => (
            <div key={m.media_id} className="flex items-center gap-3 p-2 rounded-lg bg-white/[0.03] border border-white/10">
              {m.type === "photo" ? (
                <img src={m.url} alt="" className="w-14 h-14 object-cover rounded shrink-0" />
              ) : (
                <div className="w-14 h-14 rounded shrink-0 bg-orange-500/15 border border-orange-500/30 flex items-center justify-center"><Video size={20} className="text-orange-300" /></div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-[10px] uppercase tracking-widest font-bold text-orange-300">{m.type}</p>
                <p className="text-xs truncate text-white/70">{m.url}</p>
                {m.caption && <p className="text-[11px] text-white/55 truncate italic">{m.caption}</p>}
              </div>
              <button onClick={() => remove(m.media_id)} className="p-2 rounded hover:bg-red-500/10 text-white/55 hover:text-red-400" title="Supprimer"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>

        <form onSubmit={add} className="space-y-3 border-t border-white/10 pt-4">
          <p className="text-[10px] uppercase tracking-widest text-yellow-400 font-bold">Ajouter un média</p>
          <div className="flex gap-2">
            <button type="button" onClick={() => setType("photo")} className={`flex-1 px-3 py-2 text-xs font-bold uppercase tracking-widest transition ${type === "photo" ? "bg-orange-500 text-white" : "bg-white/5 text-white/55 hover:text-white"}`} data-testid="media-type-photo">
              <Camera size={12} className="inline mr-1" /> Photo
            </button>
            <button type="button" onClick={() => setType("video")} className={`flex-1 px-3 py-2 text-xs font-bold uppercase tracking-widest transition ${type === "video" ? "bg-orange-500 text-white" : "bg-white/5 text-white/55 hover:text-white"}`} data-testid="media-type-video">
              <Video size={12} className="inline mr-1" /> Vidéo
            </button>
          </div>
          {type === "photo" ? (
            <ImageUpload
              value={url}
              onChange={(u) => setUrl(u)}
              kind="arnak"
              aspect="wide"
              label="Photo"
              hint="JPG/PNG/WEBP · max 5 Mo"
              testId="media-photo-upload"
            />
          ) : (
            <div>
              <label className={lbl}>URL de la vidéo (YouTube / Facebook / TikTok / Instagram)</label>
              <input
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                className={inp}
                placeholder="https://youtube.com/watch?v=… ou facebook.com/reel/…"
                data-testid="media-video-url"
                required
              />
              <p className="text-[10px] text-white/40 mt-1">Le lien sera automatiquement converti en lecteur intégré.</p>
            </div>
          )}
          <div>
            <label className={lbl}>Légende (optionnelle)</label>
            <input type="text" value={caption} onChange={(e) => setCaption(e.target.value)} className={inp} placeholder="ex: Aperçu live" maxLength={200} />
          </div>
          <button type="submit" disabled={saving || !url.trim()} className="w-full bg-orange-500 hover:bg-orange-400 text-white font-black uppercase tracking-widest text-xs py-3 mt-2 disabled:opacity-50 inline-flex items-center justify-center gap-2" data-testid="media-add-submit">
            <Plus size={13} /> {saving ? "Ajout…" : "Ajouter"}
          </button>
        </form>
      </div>
    </div>
  );
};

const CrudForm = ({ kind, cols, initial, onClose, reload }) => {
  const idKey = { members: "member_id", events: "event_id", gallery: "photo_id", videos: "video_id", news: "news_id", partners: "partner_id", products: "product_id" }[kind];
  const isEdit = Boolean(initial[idKey]);
  const [form, setForm] = useState(() => {
    const f = { ...initial };
    if (f.event_date && typeof f.event_date === "string") f.event_date = f.event_date.replace(" ", "T").slice(0, 16);
    if (f.published_at && typeof f.published_at === "string") f.published_at = f.published_at.replace(" ", "T").slice(0, 16);
    // Défauts pour la section vidéos/sons
    if (kind === "videos") {
      f.media_type = f.media_type || "video";
      if (f.media_type === "audio" && !f.audio_source) f.audio_source = "upload";
    }
    return f;
  });
  const [submitting, setSubmitting] = useState(false);

  const isAudio = kind === "videos" && form.media_type === "audio";

  const submit = async (e) => {
    e.preventDefault();
    // Validation audio : il faut au moins un fichier ou une URL embed
    if (kind === "videos") {
      if (form.media_type === "audio") {
        if (form.audio_source === "upload") {
          if (!form.audio_file_url) { toast.error("Choisis un fichier audio à uploader"); return; }
          form.embed_url = null;
        } else {
          if (!form.embed_url) { toast.error("Colle l'URL " + form.audio_source); return; }
          form.audio_file_url = null;
        }
      } else {
        // Vidéo : embed_url obligatoire
        if (!form.embed_url) { toast.error("Colle l'URL embed de la vidéo"); return; }
        form.audio_source = null;
        form.audio_file_url = null;
      }
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (isEdit) { payload._action = "update"; payload[idKey] = initial[idKey]; }
      await api.post(`/admin/arnak/${kind}`, payload);
      toast.success(isEdit ? "Mis à jour" : "Créé"); reload(); onClose();
    } catch (e) { toast.error(e?.response?.data?.error || "Erreur"); }
    finally { setSubmitting(false); }
  };

  // Filtre les colonnes selon le mode pour la section vidéos
  const visibleCols = kind === "videos"
    ? cols.filter((c) => {
        if (c.key === "embed_url") return !isAudio || (form.audio_source && form.audio_source !== "upload");
        if (c.videoOnly) return !isAudio;
        return true;
      })
    : cols;

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-zinc-950 border border-white/15 rounded-xl max-w-xl w-full p-6 max-h-[90vh] overflow-y-auto space-y-3" data-testid={`arnak-${kind}-form`}>
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-bold text-lg">{isEdit ? "Modifier" : "Nouveau"}</h3>
          <button type="button" onClick={onClose} className="text-white/50 hover:text-white"><X size={18} /></button>
        </div>

        {/* Sélecteur Vidéo / Audio (uniquement pour la section "videos") */}
        {kind === "videos" && (
          <div className="grid grid-cols-2 gap-2 mb-1">
            <button
              type="button"
              onClick={() => setForm({ ...form, media_type: "video" })}
              className={`px-3 py-3 rounded-lg border text-xs font-black uppercase tracking-widest transition ${form.media_type === "video" ? "bg-yellow-400 text-black border-yellow-400" : "bg-white/5 text-white/55 border-white/10 hover:text-white"}`}
              data-testid="video-type-video"
            >
              <Video size={14} className="inline mr-1.5 -mt-0.5" /> Vidéo
            </button>
            <button
              type="button"
              onClick={() => setForm({ ...form, media_type: "audio", audio_source: form.audio_source || "upload" })}
              className={`px-3 py-3 rounded-lg border text-xs font-black uppercase tracking-widest transition ${form.media_type === "audio" ? "bg-yellow-400 text-black border-yellow-400" : "bg-white/5 text-white/55 border-white/10 hover:text-white"}`}
              data-testid="video-type-audio"
            >
              <Headphones size={14} className="inline mr-1.5 -mt-0.5" /> Son
            </button>
          </div>
        )}

        {/* Source audio (uniquement si mode audio) */}
        {isAudio && (
          <div>
            <label className={lbl}>Source audio</label>
            <select
              value={form.audio_source || "upload"}
              onChange={(e) => setForm({ ...form, audio_source: e.target.value, embed_url: "", audio_file_url: "" })}
              className={inp}
              data-testid="audio-source"
            >
              <option value="upload">📤 Upload fichier (MP3/WAV)</option>
              <option value="direct">🔗 URL directe (.mp3)</option>
              <option value="soundcloud">☁️ SoundCloud</option>
              <option value="spotify">🟢 Spotify (track)</option>
              <option value="audiomack">🎧 Audiomack</option>
              <option value="youtube">▶️ YouTube (audio uniquement)</option>
            </select>
            <p className="text-[10px] text-white/45 mt-1">
              {form.audio_source === "upload" && "Le fichier sera stocké sur ton serveur OVH."}
              {form.audio_source === "direct" && "Lien direct vers un fichier MP3 (ex: hébergement perso)."}
              {form.audio_source === "soundcloud" && "Colle l'URL du morceau : https://soundcloud.com/artiste/titre"}
              {form.audio_source === "spotify" && "Colle l'URL du track : https://open.spotify.com/track/…"}
              {form.audio_source === "audiomack" && "Colle l'URL du morceau Audiomack."}
              {form.audio_source === "youtube" && "URL YouTube classique — l'extrait sera affiché en mode audio."}
            </p>
          </div>
        )}

        {/* Upload audio (uniquement si mode audio + source upload) */}
        {isAudio && form.audio_source === "upload" && (
          <div>
            <label className={lbl}>Fichier audio</label>
            <AudioUpload
              value={form.audio_file_url || ""}
              onChange={(url) => setForm({ ...form, audio_file_url: url })}
              testId="upload-audio"
            />
          </div>
        )}

        {/* URL directe (uniquement si mode audio + source direct) */}
        {isAudio && form.audio_source === "direct" && (
          <div>
            <label className={lbl}>URL directe du fichier .mp3</label>
            <input
              type="url"
              value={form.embed_url || ""}
              onChange={(e) => setForm({ ...form, embed_url: e.target.value })}
              className={inp}
              placeholder="https://example.com/track.mp3"
              required
            />
          </div>
        )}

        {/* URL provider (uniquement si mode audio + provider externe) */}
        {isAudio && ["soundcloud","spotify","audiomack","youtube"].includes(form.audio_source) && (
          <div>
            <label className={lbl}>URL {form.audio_source}</label>
            <input
              type="url"
              value={form.embed_url || ""}
              onChange={(e) => setForm({ ...form, embed_url: e.target.value })}
              className={inp}
              placeholder={
                form.audio_source === "soundcloud" ? "https://soundcloud.com/artiste/titre" :
                form.audio_source === "spotify"    ? "https://open.spotify.com/track/…" :
                form.audio_source === "audiomack"  ? "https://audiomack.com/artiste/song/…" :
                "https://www.youtube.com/watch?v=…"
              }
              required
            />
          </div>
        )}

        {visibleCols.map((c) => {
          const isImage = ["photo_url", "image_url", "thumbnail_url"].includes(c.key);
          if (isImage) {
            return (
              <ImageUpload
                key={c.key}
                value={form[c.key] || ""}
                onChange={(url) => setForm({ ...form, [c.key]: url })}
                kind="arnak"
                aspect={c.key === "image_url" && kind === "events" ? "wide" : c.key === "image_url" && kind === "gallery" ? "square" : "square"}
                label={isAudio && c.key === "thumbnail_url" ? "Pochette (optionnelle)" : c.label}
                hint={isAudio && c.key === "thumbnail_url" ? "Si vide, le logo ARNAK est utilisé" : "JPG/PNG/WEBP · max 5 Mo"}
                testId={`upload-${kind}-${c.key}`}
              />
            );
          }
          return (
          <div key={c.key}>
            <label className={lbl}>{c.label} {c.required && <span className="text-red-400">*</span>}</label>
            {c.type === "textarea" ? (
              <textarea required={c.required} value={form[c.key] ?? ""} onChange={(e) => setForm({ ...form, [c.key]: e.target.value })} rows={3} className={inp} placeholder={c.placeholder} />
            ) : c.type === "checkbox" ? (
              <label className="inline-flex items-center gap-2 mt-1"><input type="checkbox" checked={!!form[c.key]} onChange={(e) => setForm({ ...form, [c.key]: e.target.checked })} className="accent-yellow-400" /> <span className="text-sm">Oui</span></label>
            ) : (
              <input required={c.required} type={c.type || "text"} value={form[c.key] ?? (c.default ?? "")} onChange={(e) => setForm({ ...form, [c.key]: e.target.value })} className={inp} placeholder={c.placeholder} />
            )}
          </div>
          );
        })}
        <button type="submit" disabled={submitting} className="w-full bg-yellow-400 hover:bg-yellow-300 text-black font-black uppercase tracking-widest text-xs py-3 mt-3 disabled:opacity-50" data-testid={`arnak-${kind}-submit`}>
          {submitting ? "Enregistrement…" : isEdit ? "Mettre à jour" : "Créer"}
        </button>
      </form>
    </div>
  );
};

// ─── TICKETS TAB ───
const TicketsTab = ({ tickets, reload }) => {
  const STATUSES = { pending: "En attente", confirmed: "Confirmée", paid: "Payée", cancelled: "Annulée" };
  const updateStatus = async (id, status) => {
    try { await api.post(`/admin/arnak/tickets/${id}/status`, { status }); toast.success(STATUSES[status]); reload(); }
    catch { toast.error("Erreur"); }
  };
  if (tickets.length === 0) return <div className="text-center py-12 text-white/40 text-sm border border-dashed border-white/10 rounded-xl">Pas encore de réservations</div>;
  return (
    <div className="space-y-2">
      {tickets.map((t) => (
        <div key={t.ticket_id} className={`p-4 rounded-lg border ${t.status === "pending" ? "bg-yellow-400/[0.04] border-yellow-400/30" : t.status === "cancelled" ? "bg-white/[0.01] border-white/5 opacity-60" : "bg-white/[0.02] border-white/10"}`} data-testid={`arnak-ticket-${t.ticket_id}`}>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="min-w-0">
              <p className="font-bold truncate">{t.buyer_name} <span className="text-yellow-300 ml-2">×{t.qty}</span></p>
              <p className="text-xs text-white/55">{t.event_title} · {t.buyer_phone}{t.buyer_email ? ` · ${t.buyer_email}` : ""}</p>
              {t.total_ar && <p className="text-xs text-white/45 mt-0.5">Total : <span className="text-yellow-300 font-bold">{new Intl.NumberFormat("fr-FR").format(t.total_ar)} Ar</span></p>}
              {t.note && <p className="text-xs text-white/65 italic mt-1">"{t.note}"</p>}
            </div>
            <div className="flex items-center gap-1">
              <select value={t.status} onChange={(e) => updateStatus(t.ticket_id, e.target.value)}
                className="bg-white/5 border border-white/10 rounded px-2 py-1.5 text-xs">
                {Object.entries(STATUSES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default AdminArnakPanel;
